import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const [activeTimeline, setActiveTimeline] = useState(0);
  const [selectedLeader, setSelectedLeader] = useState(null);

  // Hero Section
  const HeroSection = () => (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h1 className="text-5xl font-bold mb-6">
              Empowering Businesses with Technology
            </h1>
            <p className="text-gray-600 text-lg mb-8">
              Plexgate delivers reliable tech solutions, enhancing your business with IT support, cloud services, cybersecurity, and innovative development for success.
            </p>
            <Link
              to="/contact"
              className="inline-block border-2 border-gray-900 px-8 py-3 rounded-full hover:bg-gray-900 hover:text-white transition-colors font-medium"
            >
              Consultation
            </Link>
          </div>

          {/* Right Image */}
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800"
              alt="Technology"
              className="rounded-3xl shadow-2xl w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );

  // Hero Banner Section (NEW - Plexgate Version)
  const HeroBannerSection = () => (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920" 
          alt="Network" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <h1 className="text-5xl md:text-6xl font-bold text-white">
          We're Endless Innovators Delivering <span className="text-green-500">Custom Technology Solutions</span>
        </h1>
      </div>
    </section>
  );

  // Mission and Vision Section
  const MissionVisionSection = () => (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Images */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400"
                alt="Team"
                className="rounded-2xl shadow-lg"
              />
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400"
                alt="Professional"
                className="rounded-2xl shadow-lg mt-8"
              />
            </div>
          </div>

          {/* Right Content */}
          <div>
            <h2 className="text-4xl font-bold mb-6">Our Mission and Vision</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Dedicated to exceptional service, our experienced team at Plexgate is committed to empowering businesses with innovative technology and reliable support.
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  // Journey Timeline Section
  const JourneySection = () => {
    const timeline = [
      {
        title: 'OUR BEGINNING',
        description: 'Plexgate was founded by a team of visionary technology leaders who recognized the need for businesses to seamlessly integrate advanced IT solutions. What started as a dream quickly became a reality as Plexgate emerged as a trusted partner for businesses navigating the complexities of the digital age.'
      },
      {
        title: 'EARLY SUCCESS',
        description: 'Plexgate expanded its offerings, delivering advanced cloud infrastructure, automation, and IT consulting solutions. With a growing client base and a talented team, Plexgate earned a reputation for reliability, innovation, and excellence.'
      },
      {
        title: 'INNOVATION & IMPACT',
        description: 'Plexgate launched groundbreaking solutions in cybersecurity and digital transformation, empowering organizations to operate more securely and efficiently. With projects spanning healthcare, finance, and education, Plexgate made a lasting impact.'
      },
      {
        title: 'VISION FOR THE FUTURE',
        description: 'As Plexgate moves into 2026 and beyond, our mission remains clear: to empower businesses through innovative, scalable, and secure technology solutions. We are committed to staying ahead of the curve, ensuring our clients are always prepared for what comes next.',
        gradient: true
      }
    ];

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('opacity-100', 'translate-y-0');
              entry.target.classList.remove('opacity-0', 'translate-y-8');
            }
          });
        },
        { threshold: 0.2 }
      );

      document.querySelectorAll('.timeline-item').forEach((el) => observer.observe(el));

      return () => observer.disconnect();
    }, []);

    return (
      <section className="pt-4 pb-0 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">OUR JOURNEY</h2>
          <p className="text-center text-gray-600 mb-6 max-w-3xl mx-auto">
            At Plexgate, we are driven by a passion for innovation and a commitment to helping businesses embrace technology for growth, efficiency, and resilience. Here's a look at our story and milestones.
          </p>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gray-300 hidden md:block"></div>

            {/* Timeline Items */}
            {timeline.map((item, index) => (
              <div
                key={index}
                className={`timeline-item opacity-0 translate-y-8 transition-all duration-1000 mb-10 ${
                  index % 2 === 0 ? 'md:pr-1/2' : 'md:pl-1/2 md:ml-auto'
                }`}
              >
                <div className="flex flex-col md:flex-row items-center gap-8">
                  {index % 2 === 0 ? (
                    <>
                      <div className="md:w-1/2 md:pr-8">
                        <div className={`${item.gradient ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white' : 'bg-gray-100'} rounded-lg p-6 shadow-lg hover:scale-105 transition-transform`}>
                          <h3 className={`text-2xl font-bold mb-3 ${item.gradient ? 'text-white' : 'text-red-600'}`}>
                            {item.title}
                          </h3>
                          <p className={item.gradient ? 'text-white' : 'text-gray-700'}>
                            {item.description}
                          </p>
                        </div>
                      </div>
                      <div className="hidden md:block md:w-1/2 text-center">
                        <div className="w-6 h-6 bg-white border-4 border-gray-500 rounded-full mx-auto hover:scale-125 hover:bg-red-600 transition-all"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hidden md:block md:w-1/2 text-center">
                        <div className="w-6 h-6 bg-white border-4 border-gray-500 rounded-full mx-auto hover:scale-125 hover:bg-red-600 transition-all"></div>
                      </div>
                      <div className="md:w-1/2 md:pl-8">
                        <div className={`${item.gradient ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white' : 'bg-gray-100'} rounded-lg p-6 shadow-lg hover:scale-105 transition-transform`}>
                          <h3 className={`text-2xl font-bold mb-3 ${item.gradient ? 'text-white' : 'text-red-600'}`}>
                            {item.title}
                          </h3>
                          <p className={item.gradient ? 'text-white' : 'text-gray-700'}>
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Core Values Section (NEW - Plexgate Version)
  const CoreValuesSection = () => {
    const values = [
      {
        title: 'Teamwork',
        description: 'Our leadership team brings global expertise, industry experience, and dedication to empower our professionals, helping them deliver exceptional solutions that drive client success.'
      },
      {
        title: 'Customer Satisfaction',
        description: 'At Plexgate, client satisfaction is paramount. We don\'t just meet expectations—we exceed them, delivering quality technology solutions that set industry standards.'
      },
      {
        title: 'Transparency',
        description: 'We prioritize transparency through open communication with our clients, building trust and fostering long-term partnerships that grow together.'
      },
      {
        title: 'Innovation',
        description: 'We embrace innovation in every project, continuously exploring emerging technologies and creative solutions to keep our clients ahead of the curve.'
      },
      {
        title: 'Excellence',
        description: 'Our commitment to excellence means exceeding expectations, meeting deadlines, and delivering high-quality technology solutions that define who we are at Plexgate.'
      },
      {
        title: 'Integrity',
        description: 'Plexgate operates with unwavering integrity and ethical standards in everything we do, reflecting our core values and commitment to doing what\'s right.'
      }
    ];

    return (
      <section className="pt-4 pb-10 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our Core <span className="text-green-600">Values</span>
            </h2>
            <p className="text-gray-600 max-w-4xl mx-auto">
              Our core values define who we are at Plexgate, shape our company culture, and guide our approach to every project. They enable us to build lasting partnerships with clients and create opportunities for mutual growth.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow text-center"
              >
                <h3 className="text-xl font-bold mb-3 text-gray-900">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Why Choose Us Section
  const WhyChooseSection = () => (
    <section className="py-10 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Image */}
          <div>
            <img
              src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600"
              alt="Code"
              className="rounded-3xl shadow-xl"
            />
          </div>

          {/* Right Content */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-sm uppercase text-gray-600 mb-2">WHY CHOOSE US</h3>
            <p className="text-gray-700 leading-relaxed">
              At Plexgate, we understand the challenges businesses face. That's why we focus on providing solutions that bring clarity and direction. Our goal is to build meaningful partnerships, offering personalized support to address your unique needs. With us, you'll have a trusted team by your side, dedicated to helping you achieve success and navigate complexity with confidence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  // Our Impact Section
  const ImpactSection = () => (
    <section className="py-16 bg-blue-50">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <p className="text-sm uppercase text-gray-600 mb-2">OUR IMPACT</p>
        <h2 className="text-4xl font-bold mb-12">
          Empower your business to thrive, streamline operations, and unlock maximum potential.
        </h2>
      </div>
    </section>
  );

  // Our Approach Section
  const ApproachSection = () => (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left Content */}
          <div>
            <h6 className="text-sm uppercase text-gray-600 mb-4">OUR APPROACH</h6>
            <h2 className="text-4xl font-bold mb-4">
              Each client has unique needs.
            </h2>
            <h2 className="text-4xl font-bold mb-6">
              Hence, we customize every partnership to <span className="text-red-600">fit your goals.</span>
            </h2>
            <p className="text-gray-600">
              Whether you need a small pilot team or a large-scale solution, we have the resources to support your success.
            </p>
          </div>

          {/* Right Checklist */}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <span className="text-green-600 text-2xl">✔️</span>
              <div>
                <p className="text-gray-700">Looking for extra software engineers to strengthen your team?</p>
                <p className="text-green-600 font-bold text-sm uppercase mt-1">We've got you covered.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="text-green-600 text-2xl">✔️</span>
              <div>
                <p className="text-gray-700">Need multiple teams to manage different projects at once?</p>
                <p className="text-green-600 font-bold text-sm uppercase mt-1">We make it happen.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="text-green-600 text-2xl">✔️</span>
              <div>
                <p className="text-gray-700">Want a hassle-free, end-to-end project execution?</p>
                <p className="text-green-600 font-bold text-sm uppercase mt-1">Leave it to us.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Stats Section with Spinning Circles
  const StatsSection = () => (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-12">
          {/* Left Content */}
          <div className="lg:w-2/5">
            <h6 className="text-sm uppercase text-gray-600 mb-4">Our Experience</h6>
            <h2 className="text-4xl font-bold leading-tight">
              Our technology specialists empower leading companies like <span className="text-red-600">Salesforce</span>.
            </h2>
          </div>

          {/* Right Stats */}
          <div className="flex gap-8 flex-wrap justify-center lg:justify-end">
            {[
              { number: '1,250+', label: 'Projects Delivered', text: 'SUCCESSFULLY COMPLETED PROJECTS •' },
              { number: '96%', label: 'Client Retention Rate', text: 'EXCELLENT CUSTOMER LOYALTY •' },
              { number: '500+', label: 'Active Engagements', text: 'CURRENT PROJECTS IN PROGRESS •' }
            ].map((stat, index) => (
              <div key={index} className="relative w-40 h-40">
                <svg className="absolute inset-0 w-full h-full animate-spin-slow" viewBox="0 0 100 100">
                  <defs>
                    <path id={`circlePath${index}`} d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
                  </defs>
                  <text className="text-[6px] fill-gray-600 font-bold">
                    <textPath href={`#circlePath${index}`} startOffset="50%" textAnchor="middle">
                      {stat.text}
                    </textPath>
                  </text>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-3xl font-bold">{stat.number}</div>
                  <div className="text-xs text-gray-600 text-center mt-2">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // Founders Section
  const FoundersSection = () => {
    const founders = [
      {
        name: 'Dr. Enike Opogah',
        title: 'CEO/Co-Founder',
        image: '/images/Pastor.jpeg',
        shortDesc: 'Dr. Enike is a dynamic technology professional and community leader based in London, Ontario.',
        fullDesc: 'Dr. Enike is a dynamic technology professional with extensive experience in IT automation and cloud infrastructure engineering. Dr. Enike is deeply committed to giving back to his community and making a meaningful impact beyond the tech sphere. Dr. Enike is a proud husband to Ijeoma Opogah, and together they are raising three wonderful daughters. His multifaceted roles as a tech entrepreneur, community advocate, and devoted family man highlight his commitment to excellence in all aspects of life.'
      }
    ];

    return (
      <section className="pt-0 pb-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">Founder</h2>
          

          <div className="flex justify-center">
            {founders.map((founder, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 max-w-sm">
                <div className="w-48 h-48 mx-auto overflow-hidden border-4 border-gray-300 mb-6 shadow-lg">
                  <img src={founder.image} alt={founder.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-xl font-bold text-center mb-2">{founder.name}</h3>
                <h4 className="text-red-600 font-semibold text-center mb-4">{founder.title}</h4>
                <p className="text-gray-700 text-sm text-center mb-4">{founder.shortDesc}</p>
                <div className="text-center">
                  <button
                    onClick={() => setSelectedLeader(founder)}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    Read More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // ── Executive Team Section ──────────────────────────────────────────────
const ExecutiveTeamSection = () => {
  const executives = [
  {
    name: 'Layla Al-Rashidi',
    title: 'Chief Marketing Officer',
    image: '/images/cmo-placeholder.jpeg',
    shortDesc: 'Our CMO leads Plexgate\'s brand strategy, market positioning, and growth initiatives across all channels.',
    fullDesc: 'As Chief Marketing Officer, this individual is responsible for defining and executing Plexgate\'s overarching marketing vision. Holding a Master\'s degree in Marketing Strategy from King Abdullah University of Science and Technology, Saudi Arabia, the CMO brings a deep understanding of digital marketing, brand development, and customer acquisition strategies. They work closely with the leadership team to align marketing objectives with business goals, overseeing campaigns, content, partnerships, and public relations. Their leadership ensures Plexgate remains a recognized and trusted name in the technology industry, continually expanding its reach and impact in the marketplace.'
  },
  {
    name: 'Priya Nair',
    title: 'Chief Development Officer',
    image: '/images/cdo-placeholder.jpeg',
    shortDesc: 'Our CDO spearheads Plexgate\'s technical roadmap, software architecture, and engineering excellence.',
    fullDesc: 'The Chief Development Officer at Plexgate is the driving force behind the company\'s technical strategy and product development. Holding a Master\'s degree in Software Engineering from the Indian Institute of Technology, India, this executive oversees all aspects of software engineering, platform architecture, and development operations — ensuring that Plexgate consistently delivers cutting-edge, scalable, and robust technology solutions. The CDO fosters a culture of innovation and engineering excellence, mentoring development teams, setting quality standards, and aligning technical capabilities with the evolving needs of clients.'
  },
  {
    name: 'Akash Vineesh',
    title: 'Director of Marketing',
    image: '/images/dom-placeholder.jpeg',
    shortDesc: 'Our Director of Marketing drives campaign execution, digital presence, and client engagement strategies.',
    fullDesc: 'The Director of Marketing at Plexgate is responsible for translating the company\'s high-level marketing strategy into impactful, results-driven campaigns. Holding a Master\'s degree in Digital Marketing and Communications from Mohammed V University, Morocco, this role oversees day-to-day marketing operations including digital advertising, SEO, social media, email marketing, and content production. The Director collaborates closely with the CMO and cross-functional teams to ensure brand consistency and message clarity across every touchpoint, continuously optimizing performance and identifying new growth opportunities for Plexgate.'
  },
  {
    name: 'Kavindra Perera',
    title: 'Project Manager',
    image: '/images/pm-placeholder.jpeg',
    shortDesc: 'Our Project Manager ensures every client engagement is delivered on time, within scope, and to the highest standard.',
    fullDesc: 'Plexgate\'s Project Manager serves as the central point of coordination across all active client projects and internal initiatives. Holding a Master\'s degree in Project Management from the University of Moratuwa, Sri Lanka, this professional brings strong expertise in agile methodologies, risk management, and stakeholder communication. The Project Manager bridges the gap between technical teams and clients, translating complex requirements into actionable plans while keeping all parties informed and aligned. Their commitment to delivery excellence and proactive problem-solving ensures Plexgate consistently meets and exceeds client expectations on every engagement.'
  },
  {
    name: 'Sarah Whitmore',
    title: 'Director of Finance & Accounts',
    image: '/images/finance-placeholder.jpeg',
    shortDesc: 'Our Director of Finance & Accounts oversees Plexgate\'s financial health, budgeting, and fiscal strategy.',
    fullDesc: 'The Director of Finance & Accounts at Plexgate is responsible for managing the company\'s overall financial operations, ensuring fiscal discipline and long-term financial sustainability. Holding a Master\'s degree in Finance and Accounting from the University of Toronto, Canada, this role oversees budgeting, financial reporting, payroll, invoicing, audits, and regulatory compliance. Working closely with the executive team, the Director provides strategic financial insights that guide key business decisions and investments, ensuring Plexgate\'s resources are allocated effectively and the company remains financially strong as it continues to scale and grow.'
  },
  {
    name: 'Claire Hartley',
    title: 'Director of Business Development',
    image: '/images/bizdev-placeholder.jpeg',
    shortDesc: 'Our Director of Business Development drives Plexgate\'s growth through strategic partnerships and new market opportunities.',
    fullDesc: 'The Director of Business Development at Plexgate leads the charge in identifying, pursuing, and securing new business opportunities that align with the company\'s growth strategy. Holding a Master\'s degree in Business Administration from Ryerson University, Toronto, Canada, this role involves building and nurturing strategic partnerships, managing client relationships, and exploring new markets and revenue streams. The Director works hand-in-hand with the marketing and executive teams to develop compelling value propositions and winning proposals, playing a critical role in expanding Plexgate\'s footprint in the technology sector.'
  }
];

  return (
    <section className="pt-8 pb-10 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-4">
          Executive <span className="text-green-600">Team</span>
        </h2>
        <h3 className="text-center text-gray-500 mb-10">
          The Leaders Driving Plexgate's Growth, Strategy, and Operational Excellence.
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
          {executives.map((exec, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center w-full max-w-sm">
              <div className="w-48 h-48 overflow-hidden border-4 border-gray-300 mb-6 shadow-lg bg-gray-100 flex items-center justify-center">
                <img
                  src={exec.image}
                  alt={exec.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = `
                      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%;height:100%;background:#f3f4f6;color:#9ca3af;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                        <span style="font-size:11px;margin-top:6px;">Photo Coming Soon</span>
                      </div>`;
                  }}
                />
              </div>
              <h3 className="text-xl font-bold text-center mb-2">{exec.name}</h3>
              <h4 className="text-red-600 font-semibold text-center mb-4">{exec.title}</h4>
              <p className="text-gray-700 text-sm text-center mb-4">{exec.shortDesc}</p>
              <button
                onClick={() => setSelectedLeader(exec)}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
              >
                Read More
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

  // Leadership Team Section (Directors only)
  const LeadershipSection = () => {
    const leaders = [
      {
        name: 'Jerry Ajaegbo',
        title: 'Director of Networks',
        image: '/images/Jerry.jpeg',
        shortDesc: 'Jerry oversees the design and maintenance of secure network systems.',
        fullDesc: "Jerry has over a decade of experience in network design and implementation, ensuring robust and secure infrastructures for clients. He brings a blend of technical expertise and leadership to PlexGate. Jerry's experience spans over a decade working in various sectors that include energy, telecommunications, finance, consulting and insurance. He has gained unquestionable experience working in multi-vendor environments and with vast number of technologies. He holds a bachelor's degree in computer engineering from Covenant University and a master's degree in communications engineering and networks from the University of Birmingham. Jerry, with his wife, have two sons and reside in London Ontario where they enjoy the peace and quiet of the beautiful city they now call home."
      },
      // {
      //   name: 'Chukwuemeka Ibebugwu',
      //   title: 'Director of Mobile Solutions',
      //   image: '/images/Emeka.jpeg',
      //   shortDesc: 'Chukwuemeka serves as the Director of Mobile Solutions at Plexgate Inc.',
      //   fullDesc: "Chukwuemeka brings extensive expertise in mobile development, strategic innovation, and aligning technology with organizational objectives. Known for his ability to manage high-performing teams, he delivers impactful, mobile-first experiences. He holds a bachelor's degree in Industrial Production Engineering and a certificate in Computer Software and Database Management from Lambton College. Nearly four years ago, Chukwuemeka relocated to Canada with his wife, gaining valuable experience working with diverse clients and teams. He currently resides in London, Ontario, where he enjoys the city's peaceful and welcoming environment while spending quality time with his family."
      // },
      {
        name: 'Elijah Jim',
        title: 'Director of Innovation & Creativity',
        image: '/images/Elijah.jpeg',
        shortDesc: 'Elijah oversees the development of innovative, user-friendly, and scalable solutions that drive impact and growth.',
        fullDesc: "Elijah is dedicated to building innovative solutions that meet clients needs and exceed expectations. With his expertise in front-end and back-end development, Elijah ensures PlexGate stays ahead in the ever-evolving digital landscape. Elijah holds a BTech in Application Development and an MTech in Information Technology from Cape Peninsula University of Technology, along with a Postgraduate Diploma in IT Infrastructure from Fanshawe College, London, Ontario. With expertise in web development, networking, and IT infrastructure, he brings creativity and technical innovation to his role as Director of Innovation & Creativity, driving scalable and user-focused digital experiences at PlexGate. Now based in London, Ontario, he continues to lead impactful innovative transformations."
      }
    ];

    return (
      <section className="pt-4 pb-2 bg-white">
        <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-6">
  Leadership <span className="text-green-600">Team</span>
</h2>
<h3 className="text-1xl text-center mb-5">The Core Team Responsible for Strategy, Operations and Day-to-Day Execution.</h3>
         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
            {leaders.map((leader, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center">
                <div className="w-48 h-48 overflow-hidden border-4 border-gray-300 mb-6 shadow-lg">
                  <img src={leader.image} alt={leader.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-xl font-bold text-center mb-2">{leader.name}</h3>
                <h4 className="text-red-600 font-semibold text-center mb-4">{leader.title}</h4>
                <p className="text-gray-700 text-sm text-center mb-4">{leader.shortDesc}</p>
                <button
                  onClick={() => setSelectedLeader(leader)}
                  className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
                >
                  Read More
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Our Team Section (Other team members)
  const OurTeamSection = () => {
    const team = [
      {
        name: 'Desire Adaugo',
        title: 'UX/UI Designer',
        image: '/images/desire.jpeg',
        shortDesc: 'Desire is a thoughtful, detail-driven UI/UX designer focused on creating intuitive digital experiences.',
        fullDesc: "Desire is a thoughtful, detail-driven UI/UX designer focused on creating intuitive digital experiences that feel seamless and purposeful. With a strong emphasis on usability, clarity, and scalability, designs interfaces that balance user needs with modern technology. His experience spans user research, wireframing, prototyping, and interface design across web and mobile platforms, ensuring each product is both functional and engaging. Known for a clean design approach, Desire brings structure, creativity, and intention to every project. Through this work, complex ideas are transformed into thoughtful, user-centered experiences that are both functional and easy to use."
      },
      {
        name: 'Emmanuel Nwobodo',
        title: 'Graphic Designer',
        image: '/images/emma.jpeg',
        shortDesc: 'Emmanuel is a creative and detail-driven graphics designer dedicated to crafting visually compelling designs.',
        fullDesc: "Emmanuel is a creative and detail-driven graphics designer dedicated to crafting visually compelling designs that communicate clearly and leave a lasting impression. With a strong focus on branding, digital creatives, and visual storytelling, he consistently delivers designs that align with brand identity, purpose, and audience needs. His experience spans social media designs, flyers, promotional materials, and digital visuals, ensuring every project is both impactful and visually engaging. Known for his clarity, consistency, and creative approach, Emmanuel brings fresh ideas and thoughtful execution to every brief. Through his work, he continues to create meaningful visual experiences that help brands stand out and communicate effectively in today's fast-paced digital landscape."
      },
      {
        name: 'Carol James',
        title: 'Human Resources Specialist',
        image: '/images/carole.jpeg',
        shortDesc: 'Carol is a people-focused Human Resources Specialist committed to building strong, engaged, and purpose-driven teams.',
        fullDesc: "Carol James is a passionate Human Resources Specialist driven by purpose, people, and progress. She holds a Postgraduate Degree in Digital Communication and Media/Multimedia from Fanshawe College, as well as a Bachelor of Arts in Digital Communication and Media/Multimedia from Afe Babalola University. With a strong focus on employee engagement and organizational growth, Carol supports teams in building authentic workplace cultures that inspire performance, connection, and long-term success. At PlexGate Inc., she contributes to the development of people-centered strategies, enhanced employee experiences, and leadership support initiatives that strengthen high-performing teams. Carol is known for blending empathy with strategic thinking, transforming HR initiatives into measurable outcomes that reinforce culture and drive organizational impact. She believes great HR goes beyond policies, it is rooted in people, communication, and continuous growth."
      },
     
    ];

    return (
      <section className="pt-4 pb-8 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-6">
  Our <span className="text-green-600">Team</span>
</h2>
          <h3 className="text-1xl text-center mb-5">A talented Network of professionals, engineers, designers and specialists driving our projects foward.</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center">
                <div className="w-48 h-48 overflow-hidden border-4 border-gray-300 mb-6 shadow-lg">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-xl font-bold text-center mb-2">{member.name}</h3>
                <h4 className="text-red-600 font-semibold text-center mb-4">{member.title}</h4>
                <p className="text-gray-700 text-sm text-center mb-4">{member.shortDesc}</p>
                <button
                  onClick={() => setSelectedLeader(member)}
                  className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
                >
                  Read More
                </button>
              </div>
            ))}
          </div>

          {/* Modal */}
          {selectedLeader && (
            <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-lg p-8 max-w-3xl relative max-h-[90vh] overflow-y-auto">
                <button
                  onClick={() => setSelectedLeader(null)}
                  className="absolute top-4 right-4 text-gray-600 hover:text-red-600 text-2xl"
                >
                  ✖
                </button>
                <div className="text-center pt-4">
                  <div className="w-64 h-64 mx-auto overflow-hidden border-4 border-gray-300 mb-6">
                    <img src={selectedLeader.image} alt={selectedLeader.name} className="w-full h-full object-cover" />
                  </div>
                  <h2 className="text-3xl font-bold mb-2">{selectedLeader.name}</h2>
                  <h3 className="text-lg text-red-600 font-semibold mb-4">{selectedLeader.title}</h3>
                  <p className="text-gray-700 whitespace-pre-line">{selectedLeader.fullDesc}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  };

  // CTA Banner Section (NEW - Plexgate Version)
  const CTABannerSection = () => (
    <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920" 
          alt="Team" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to <span className="text-green-500">Begin</span> Your <span className="text-green-500">Digital Transformation</span> with Plexgate?
          </h2>
          <p className="text-white text-lg mb-8">
            Whether you're seeking innovative technology solutions or looking to partner with a forward-thinking IT team, Plexgate is here to help. Let's discuss how we can transform your vision into reality and accelerate your business success.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
          >
            Contact Us Today →
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <div className="about-page">
      <HeroSection />
      <HeroBannerSection />
      <MissionVisionSection />
      <JourneySection />
      <CoreValuesSection />
      <FoundersSection />
      <ExecutiveTeamSection /> 
      <LeadershipSection />
      <OurTeamSection />
      <WhyChooseSection />
      <ImpactSection />
      <ApproachSection />
      <StatsSection />
      <CTABannerSection />
    </div>
  );
};

// Add spinning animation to tailwind config
const style = document.createElement('style');
style.textContent = `
  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .animate-spin-slow {
    animation: spin-slow 10s linear infinite;
  }
`;
document.head.appendChild(style);

export default About;